/**
 * Cartao de um modelo na grade.
 *
 * Decisoes nao obvias:
 *  1. O cartao INTEIRO abre a previa, mas sem aninhar controles dentro de um
 *     botao (HTML invalido e leitor de tela confuso): o nome e o botao, com um
 *     pseudo-elemento esticado por cima do cartao; o "Comparar" fica acima
 *     dele (`relative z-10`) e continua clicavel e focavel sozinho.
 *  2. Quatro numeros e so quatro: receberam, responderam, clicaram (atenderam,
 *     na voz) e pagaram. Cada um diz o denominador no `title`, em termos de
 *     negocio. O resto mora na aba Desempenho da previa.
 *  3. O selo "Resposta → vendedora" aparece em todo modelo com botao de
 *     resposta: quem monta a regua precisa ver, sem abrir, que aquele toque
 *     entrega o lead ao comercial e para a regua.
 *  4. O trecho usa `destacarVariaveis`, a mesma marca da previa do aparelho.
 *  5. Tudo que tem dica (`title`) ganha `relative`: elemento posicionado que
 *     vem depois no DOM pinta por cima do pseudo-elemento esticado, entao a
 *     dica aparece no hover (e o clique ali nao abre a previa, de proposito).
 */

import { Badge, CompanyLogo, Icon, destacarVariaveis } from '../../../components/ui';
import { formatCount, formatDateTime, formatRatio } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { AMOSTRA_MINIMA, geraCredito, type PosicaoNoGrupo } from '../desempenho';
import type { MessageTemplate } from '../types';
import { CANAL_ICONE, CATEGORIA_LABEL, CATEGORIA_TOM, SAIDA_LABEL, STATUS_TOM, VOLUME_CURTO, descreverPublico, statusLabel } from '../vocabulario';
import { Variacao } from './Variacao';

function Numero({ rotulo, valor, detalhe, dica }: { readonly rotulo: string; readonly valor: string; readonly detalhe?: string; readonly dica: string }): JSX.Element {
  return (
    <div className="relative min-w-0" title={dica}>
      <dt className="truncate text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{rotulo}</dt>
      <dd className="truncate text-sm font-semibold tabular-nums text-ink">{valor}</dd>
      {detalhe === undefined ? null : <dd className="truncate text-[11px] tabular-nums text-ink-subtle">{detalhe}</dd>}
    </div>
  );
}

export function CartaoModelo({
  template,
  empresa,
  posicao,
  selecionado,
  podeSelecionar,
  onAbrir,
  onComparar,
}: {
  readonly template: MessageTemplate;
  /** Dona do modelo; `null` = global. */
  readonly empresa: CompanySummary | null;
  readonly posicao: PosicaoNoGrupo | null;
  readonly selecionado: boolean;
  /** `false` quando ja ha dois escolhidos e este nao e um deles. */
  readonly podeSelecionar: boolean;
  readonly onAbrir: (template: MessageTemplate) => void;
  readonly onComparar: (template: MessageTemplate) => void;
}): JSX.Element {
  const p = template.performance;
  const a = template.previousPerformance;
  const credita = geraCredito(template);
  const temResposta = template.buttons.some((b) => b.kind === 'reply') || template.channel === 'whatsapp_evolution';
  const usadoEm = template.usedIn.length === 0 ? 'Não está ligado a nenhuma régua ou disparo' : template.usedIn.join(' · ');
  const idComparar = `comparar-${template.id}`;

  return (
    <li className="min-w-0">
      <article
        className={`relative flex h-full flex-col gap-3 rounded-2xl border bg-surface p-4 shadow-soft transition-colors hover:border-brand/50 focus-within:border-brand ${
          selecionado ? 'border-brand ring-1 ring-brand/40' : 'border-line'
        }`}
      >
        <div className="flex w-full items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span aria-hidden="true" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand/10 text-brand">
              <Icon name={CANAL_ICONE[template.channel]} size="sm" />
            </span>
            <span className="min-w-0">
              <button
                type="button"
                onClick={() => onAbrir(template)}
                className="block max-w-full truncate text-left font-mono text-sm font-semibold text-ink after:absolute after:inset-0 after:rounded-2xl hover:text-brand"
                title={`Abrir prévia de ${template.name}`}
              >
                {template.name}
              </button>
              <span className="block truncate text-xs text-ink-subtle">
                {SAIDA_LABEL[template.channel]} · {descreverPublico(template)}
              </span>
            </span>
          </div>
          <Badge tone={STATUS_TOM[template.status]} dot className="shrink-0">
            {statusLabel(template.status, template.channel)}
          </Badge>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge tone={CATEGORIA_TOM[template.category]}>{CATEGORIA_LABEL[template.category]}</Badge>
          {posicao?.melhor === true ? (
            <span className="relative" title={`Maior taxa de pagamento em ${posicao.rotuloGrupo}, entre ${posicao.de} modelos com pelo menos ${AMOSTRA_MINIMA} envios no período.`}>
              <Badge tone="good" icon="estrela">
                Melhor da categoria
              </Badge>
            </span>
          ) : null}
          {temResposta ? (
            <span className="relative" title="Quem responde cai no WhatsApp do comercial, com a vendedora, e a régua para na hora para esse lead.">
              <Badge tone="violet" icon="resposta">
                Resposta → vendedora
              </Badge>
            </span>
          ) : null}
          {template.variableMap.length > 0 ? <Badge tone="neutral">Variáveis {'{{n}}'}</Badge> : null}
        </div>

        <p className="line-clamp-3 w-full whitespace-pre-line text-sm leading-relaxed text-ink-muted">
          {template.channel === 'email' && template.subject !== undefined ? (
            <>
              <span className="font-medium text-ink">{destacarVariaveis(template.subject)}</span>
              {' — '}
            </>
          ) : null}
          {destacarVariaveis(template.body)}
        </p>

        {p.sent === 0 ? (
          <p className="rounded-lg border border-dashed border-edge px-3 py-2 text-xs text-ink-subtle">
            {template.status === 'pendente'
              ? 'Sem envios: aguarda a Meta.'
              : template.status === 'rejeitado'
                ? 'Sem envios: recusado pela Meta.'
                : template.usedIn.length === 0
                  ? 'Sem envios: não está ligado a nada.'
                  : 'Nenhum envio no período.'}
          </p>
        ) : (
          <div className="flex flex-col gap-1.5 rounded-xl border border-line bg-elevated px-3 py-2.5">
            <dl className="grid grid-cols-4 gap-2">
              <Numero rotulo={template.channel === 'voice' ? 'Ligações' : 'Receberam'} valor={formatCount(p.sent)} dica={`${formatCount(p.sent)} ${VOLUME_CURTO[template.channel]} no período.`} />
              <Numero rotulo="Responderam" valor={formatRatio(p.replied, p.delivered)} detalhe={formatCount(p.replied)} dica={`${formatCount(p.replied)} de ${formatCount(p.delivered)} que receberam responderam e caíram com a vendedora; a régua parou para eles.`} />
              {template.channel === 'voice' ? (
                <Numero rotulo="Atenderam" valor={formatRatio(p.delivered, p.sent)} detalhe={formatCount(p.delivered)} dica={`${formatCount(p.delivered)} de ${formatCount(p.sent)} ligações foram atendidas.`} />
              ) : (
                <Numero
                  rotulo="Clicaram"
                  valor={p.clicked === null ? '—' : formatRatio(p.clicked, p.delivered)}
                  detalhe={p.clicked === null ? undefined : formatCount(p.clicked)}
                  dica={p.clicked === null ? 'Sem link medido neste modelo.' : `${formatCount(p.clicked)} pessoas clicaram no link (medido pelo redirecionador da régua).`}
                />
              )}
              <Numero
                rotulo="Pagaram"
                valor={credita ? formatCount(p.paid) : '—'}
                detalhe={credita ? formatRatio(p.paid, p.sent) : 'sem crédito'}
                dica={
                  credita
                    ? `${formatCount(p.paid)} pagaram em até 7 dias com o crédito neste toque (último contato).`
                    : 'Este público não gera crédito próprio: a comissão fica com o toque da régua que trouxe o lead.'
                }
              />
            </dl>
            <p className="flex flex-wrap items-center gap-x-2 text-[11px] text-ink-subtle">
              {credita ? 'Pagamentos' : 'Envios'}
              <Variacao atual={credita ? p.paid : p.sent} anterior={a === null ? null : credita ? a.paid : a.sent} compacto />
              <span>vs período anterior</span>
            </p>
          </div>
        )}

        <dl className="mt-auto flex w-full flex-col gap-1.5 border-t border-line pt-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <dt className="sr-only">Empresa</dt>
            <dd className="flex min-w-0 items-center gap-2 text-ink">
              {empresa === null ? (
                <Badge tone="brand" icon="empresa">
                  Global
                </Badge>
              ) : (
                <>
                  <CompanyLogo company={empresa} size="sm" />
                  <span className="truncate font-medium">{empresa.name}</span>
                </>
              )}
            </dd>
            <dd className="relative z-10 shrink-0">
              <label
                htmlFor={idComparar}
                className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium transition-colors ${
                  selecionado ? 'border-brand bg-brand/10 text-brand' : 'border-line text-ink-muted hover:border-brand/60'
                } ${podeSelecionar || selecionado ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                title={podeSelecionar || selecionado ? 'Escolha dois modelos para comparar lado a lado' : 'Já há dois modelos escolhidos: desmarque um para trocar'}
              >
                <input
                  id={idComparar}
                  type="checkbox"
                  checked={selecionado}
                  disabled={!podeSelecionar && !selecionado}
                  onChange={() => onComparar(template)}
                  className="h-3.5 w-3.5 accent-[rgb(var(--color-brand))]"
                />
                Comparar
              </label>
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="shrink-0 text-ink-subtle">Usado em:</dt>
            <dd className="relative min-w-0 truncate text-ink-muted" title={usadoEm}>
              {usadoEm}
            </dd>
          </div>
          <div>
            <dt className="sr-only">Última atualização</dt>
            <dd className="text-ink-subtle">
              Atualizado em {formatDateTime(template.updatedAt)} · v{template.versions[template.versions.length - 1]?.version ?? 1}
            </dd>
          </div>
        </dl>
      </article>
    </li>
  );
}
